import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiPlay, FiVolume2, FiFileText, FiBookOpen, FiCalendar, 
  FiDownload, FiPlus, FiEdit, FiStar, 
  FiAlertCircle, FiUser, FiVideo, FiMessageCircle,
  FiTrendingUp, FiClock, FiExternalLink
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import Notification from '../common/Notification';
import EditCourseModal from '../admin/EditCourseModal';
import AddContentModal from '../admin/AddContentModal';
import VideoPlaylist from './VideoPlaylist';
import VideoPlayer from './VideoPlayer';
import CourseAttendanceRecords from './CourseAttendanceRecords';
import richTextStyles from '../../styles/richTextViewer.module.css';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import ModernBottomNav from '../common/ModernBottomNav';
import JumpToSheet from '../common/JumpToSheet';
import useActiveSection from '../common/useActiveSection';
import { useHashHighlight } from './useJumpHighlight';
import SectionRail from './SectionRail';
import styles from './CourseDetail.module.css';

// Styled components moved to CSS module - only keeping ActionButton which is still used

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  white-space: normal;
  min-width: 0;
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  line-height: 1.2;
  
  /* Safari compatibility */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  
  /* Cross-browser button reset */
  background: none;
  outline: none;
  text-decoration: none;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-light);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
  
  &.secondary {
    background-color: #1F6F78 !important;
    color: var(--text-inverse);
    border: 1px solid #1F6F78;
    
    svg {
      color: var(--text-inverse);
      stroke: var(--text-inverse);
    }
    
    &:hover:not(:disabled) {
      background-color: #195A62 !important;
      border-color: #195A62 !important;
      color: var(--text-inverse) !important;
      
      svg {
        color: var(--text-inverse) !important;
        stroke: var(--text-inverse) !important;
      }
    }
  }
  
  /* Small size variant */
  &[data-size="sm"] {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    gap: 0.375rem;
    border-radius: 0;
    font-weight: 500;
    min-width: 0;
    
    @media (max-width: 768px) {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      gap: 0.375rem;
      white-space: normal;
      min-width: 0;
      height: 2.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    @media (max-width: 480px) {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      gap: 0.375rem;
      height: 2.25rem;
      min-width: fit-content;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.625rem 1rem;
    min-width: 0;
    flex: 1;
    white-space: normal;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
    gap: 0.375rem;
    height: 2.25rem;
    min-width: 0;
  }
`;

// Styled components - only keeping ActionButton and LoadingSpinner which are still used
const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid var(--border-color);
  border-radius: 0;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// SwitchCourseContainer and SwitchCourseButton moved to CSS module


// Create a styled motion button component
// BackToTopButton removed - using GlobalScrollToTop from App.js

function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [syllabus, setSyllabus] = useState([]);
  const [slides, setSlides] = useState([]);
  const [readings, setReadings] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [highlightedSection, setHighlightedSection] = useState(null);
  const [isJumpSheetOpen, setIsJumpSheetOpen] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [addContentModalTab, setAddContentModalTab] = useState('announcements');
  // showBackToTop removed - using GlobalScrollToTop from App.js
  const isUserScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef(null);

  // Refs for scrolling to sections
  const sectionRefs = useRef({});

  // Function to extract URLs from text (fallback for legacy content)
  const extractUrlsFromText = (text) => {
    if (!text) return [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex) || [];
    return urls.map((url, index) => ({
      url: url,
      label: 'Open Link' // Standardized fallback label
    }));
  };

  const handleEditCourse = () => {
    setShowEditModal(true);
  };

  const handleManageContent = (tab = 'announcements') => {
    setAddContentModalTab(tab);
    setShowAddContentModal(true);
  };

  const scrollToSection = useCallback((sectionId, options = { bypassUserScroll: false }) => {
    // Try anchor ID first (section-{id}), then fallback to section element
    const anchorId = `section-${sectionId}`;
    const anchorElement = document.getElementById(anchorId);
    const sectionElement = sectionRefs.current[sectionId] || document.getElementById(sectionId);
    const targetElement = anchorElement || sectionElement;
    
    if (targetElement) {
      // Set programmatic scroll flag to prevent observer from causing jump-back
      if (isProgrammaticScrollRef?.current !== undefined) {
        isProgrammaticScrollRef.current = true;
      }
      
      // Set highlighted section for visual feedback
      setHighlightedSection(sectionId);
      
      // Add highlighted class for CSS module styling
      if (sectionElement) {
        sectionElement.classList.add(styles.highlighted);
      }
      
      // Calculate offset to account for sticky header/rail
      const isMobile = window.innerWidth <= 768;
      const offset = isMobile ? 100 : 120;
      
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      if (options.bypassUserScroll || !isUserScrollingRef.current) {
        const targetPosition = targetElement.offsetTop - offset;
        
        window.scrollTo({
          top: targetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      }
      
      // Clear programmatic scroll flag after scroll completes
      setTimeout(() => {
        if (isProgrammaticScrollRef?.current !== undefined) {
          isProgrammaticScrollRef.current = false;
        }
      }, prefersReducedMotion ? 50 : 400); // Faster update for smoother highlighting
      
      // Remove highlight after animation completes (800ms for CSS animation)
      setTimeout(() => {
        setHighlightedSection(null);
        if (sectionElement) {
          sectionElement.classList.remove(styles.highlighted);
        }
      }, 800);
    } else {
      console.warn('❌ Section not found:', sectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: refs (isProgrammaticScrollRef, isUserScrollingRef, sectionRefs) are stable, setHighlightedSection is stable, styles.highlighted is a constant

  const handleVideoSelect = (recording) => {
    setSelectedVideo(recording);
    
    // Scroll to the "Now Playing" section after a delay to ensure it's rendered
    setTimeout(() => {
      // Try multiple methods to find the Now Playing section
      let nowPlayingSection = document.querySelector('[data-section="now-playing"]');
      
      // Fallback: try to find by the "Now Playing" text
      if (!nowPlayingSection) {
        const sections = document.querySelectorAll('section');
        for (let section of sections) {
          const title = section.querySelector('h2');
          if (title && title.textContent.includes('Now Playing')) {
            nowPlayingSection = section;
            break;
          }
        }
      }
      
      console.log('Looking for Now Playing section:', nowPlayingSection);
      
      if (nowPlayingSection) {
        // Use the same reliable scroll method as scrollToSection
        nowPlayingSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
        
        // Fallback: manual scroll calculation with proper browser compatibility
        setTimeout(() => {
          const elementRect = nowPlayingSection.getBoundingClientRect();
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
          const absoluteElementTop = elementRect.top + currentScrollY;
          
          // Calculate offset to account for sticky header
          const isMobile = window.innerWidth <= 768;
          const headerHeight = isMobile ? 56 : 64;
          const offset = headerHeight + 20; // Extra 20px for breathing room
          const scrollPosition = absoluteElementTop - offset;
          
          // Only adjust if the element is not properly positioned
          if (Math.abs(elementRect.top - offset) > 50) {
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        console.warn('Now Playing section not found');
      }
    }, 400); // Increased delay to account for framer-motion animation (delay: 0.3s = 300ms)
  };

  // scrollToTop removed - using GlobalScrollToTop from App.js

  // Handle scroll event to show/hide back-to-top button
  // handleScroll removed - using GlobalScrollToTop from App.js

  // Scroll event listener removed - using GlobalScrollToTop from App.js


  // Check if current user can add content to this course
  const canAddContent = () => {
    if (!user || !course) return false;
    
    // Admin can add content to any course
    if (user.role === 'admin') return true;
    
    // Instructor can only add content to courses they're assigned to
    if (user.role === 'instructor') {
      return course.instructor_id === user.id;
    }
    
    return false;
  };

  const handleCourseUpdated = (updatedCourse) => {
    setCourse(updatedCourse);
    setShowEditModal(false);
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: 'Course updated successfully!'
    });
  };


  const fetchCourseData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Note: SharePoint recordings will come from the database via the API
      
      // Fetch course details first
      const courseResponse = await apiService.getCourse(courseId);
      if (courseResponse.success) {
        setCourse(courseResponse.course);
      } else {
        throw new Error('Failed to fetch course details');
      }
      
      // Fetch course content
      try {
        const contentResponse = await apiService.getCourseContent(courseId);
        if (contentResponse.success) {
          // Use recordings from the API (including SharePoint recordings)
          setRecordings(contentResponse.content.recordings || []);
          setAnnouncements(contentResponse.content.announcements || []);
          setSyllabus(contentResponse.content.syllabus || []);
          setSlides(contentResponse.content.slides || []);
          setReadings(contentResponse.content.readings || []);
        }
      } catch (error) {
        console.log('Course content not available:', error.message);
        // Set empty arrays as fallback
        setRecordings([]);
        setAnnouncements([]);
        setSyllabus([]);
        setSlides([]);
        setReadings([]);
      }
      
    } catch (error) {
      console.error('Error fetching course data:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch course data'
      });
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
    }
  }, [courseId, fetchCourseData]);

  // Check for manageContent query parameter and show modal if present
  useEffect(() => {
    const manageContent = searchParams.get('manageContent');
    if (manageContent === 'true' && course) {
      setAddContentModalTab('announcements'); // Default to announcements
      setShowAddContentModal(true);
      // Remove the query parameter from URL to clean it up
      searchParams.delete('manageContent');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, course, setSearchParams]);

  // Define tabs based on user role and available content
  const getTabs = useCallback(() => {
    const tabs = [];
    
    // Announcements tab - always available (moved to top)
    tabs.push({
      id: 'announcements',
      label: 'Announcements',
      icon: <FiAlertCircle size={16} />
    });
    
    // Virtual classroom tab - if link exists
    if (course?.virtual_link) {
      tabs.push({
        id: 'virtual',
        label: 'Virtual Classroom',
        icon: <FiVideo size={16} />
      });
    }
    
    // WhatsApp group tab - if link exists
    if (course?.whatsapp_group_link) {
      tabs.push({
        id: 'whatsapp',
        label: 'WhatsApp Group',
        icon: <FiMessageCircle size={16} />
      });
    }
    
    // Recordings tab - always available
    tabs.push({
      id: 'recordings',
      label: 'Recordings',
      icon: <FiVolume2 size={16} />
    });
    
    // Syllabus tab - always available
    tabs.push({
      id: 'syllabus',
      label: 'Syllabus',
      icon: <FiBookOpen size={16} />
    });
    
    // Slides tab - always available
    tabs.push({
      id: 'slides',
      label: 'Slides',
      icon: <FiFileText size={16} />
    });
    
    // Readings tab - always available
    tabs.push({
      id: 'readings',
      label: 'Readings',
      icon: <FiBookOpen size={16} />
    });
    
    
    // Attendance tab - only for students (moved to bottom)
    if (user?.role === 'student') {
      tabs.push({
        id: 'attendance',
        label: 'Attendance',
        icon: <FiTrendingUp size={16} />
      });
    }
    
    // Navigation links
    tabs.push({
      id: 'my-courses',
      label: 'My Courses',
      icon: <FiBookOpen size={16} />,
      isNavigation: true,
      path: '/courses'
    });
    
    if (user?.role === 'student') {
      tabs.push({
        id: 'my-assignments',
        label: 'My Assignments',
        icon: <FiFileText size={16} />,
        isNavigation: true,
        path: '/assignments'
      });
      
      tabs.push({
        id: 'my-quizzes',
        label: 'My Quizzes',
        icon: <FiEdit size={16} />,
        isNavigation: true,
        path: '/student/quizzes'
      });
    } else if (user?.role === 'instructor' || user?.role === 'admin') {
      tabs.push({
        id: 'assignments',
        label: 'Assignments',
        icon: <FiFileText size={16} />,
        isNavigation: true,
        path: '/assignments'
      });
      
      tabs.push({
        id: 'quizzes',
        label: 'Quizzes',
        icon: <FiEdit size={16} />,
        isNavigation: true,
        path: '/instructor/quizzes'
      });
    }
    
    return tabs;
  }, [user?.role, course?.virtual_link, course?.whatsapp_group_link]);

  // Shared sections list (single source of truth) - must be after getTabs
  const sections = useMemo(() => {
    const tabs = getTabs();
    return tabs
      .filter(tab => !tab.isNavigation) // Exclude navigation links
      .map(tab => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon
      }));
  }, [getTabs]);

  // Scroll spy using IntersectionObserver - look for section IDs (not section-{id})
  const { activeSection: activeSectionId, isProgrammaticScrollRef } = useActiveSection(
    sections.map(s => ({ id: s.id })),
    120
  );
  
  // Active section ID is already in the correct format
  const displayActiveSectionId = useMemo(() => {
    return activeSectionId;
  }, [activeSectionId]);

  // Set default active tab when course data is loaded
  useEffect(() => {
    if (course && !activeTab) {
      const tabs = getTabs();
      if (tabs.length > 0) {
        setActiveTab(tabs[0].id);
      }
    }
  }, [course, activeTab, getTabs]);

  // Get section IDs for hash highlighting
  const sectionIds = useMemo(() => {
    return sections.map(s => s.id);
  }, [sections]);

  // Handle hash-based highlighting
  useHashHighlight(sectionIds);

  // Handle section select from Jump sheet (mobile and desktop)
  const handleSectionSelect = useCallback((sectionId) => {
    const tabs = getTabs();
    const tab = tabs.find(t => t.id === sectionId);
    if (tab?.isNavigation && tab?.path) {
      navigate(tab.path);
      return;
    }

    setActiveTab(sectionId);
    // Scroll to the section immediately
    setTimeout(() => {
      scrollToSection(sectionId, { bypassUserScroll: true });
    }, 150);
  }, [getTabs, navigate, scrollToSection]);

  const markUserScrolling = useCallback(() => {
    isUserScrollingRef.current = true;
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    userScrollTimeoutRef.current = window.setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 200);
  }, []);

  useEffect(() => {
    const events = ['wheel', 'scroll', 'touchmove'];
    events.forEach((event) => window.addEventListener(event, markUserScrolling, { passive: true }));
    return () => {
      events.forEach((event) => window.removeEventListener(event, markUserScrolling));
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [markUserScrolling]);

  // Handle Jump sheet open/close
  const handleOpenJumpSheet = useCallback(() => {
    setIsJumpSheetOpen(true);
  }, []);

  const handleCloseJumpSheet = useCallback(() => {
    setIsJumpSheetOpen(false);
  }, []);

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <LoadingSpinner />
          <p>Loading course content...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className={styles.pageContainer}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Course not found</p>
        </div>
      </div>
    );
  }



  // If showing content management, render that instead of course detail
  if (showAddContentModal && course) {
    return (
      <AddContentModal
        onClose={() => setShowAddContentModal(false)}
        course={course}
        initialTab={addContentModalTab}
        onContentAdded={(action) => {
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: action === 'updated' ? 'Content updated successfully!' : 'Content added successfully!'
          });
          // Refresh course data to show new content
          fetchCourseData();
        }}
      />
    );
  }

  return (
    <div className={`${styles.pageContainer} course-detail-page`} role="main" aria-label="Course details">
      <header className={styles.appHeader} role="banner">
        <h1 className={styles.courseTitle}>{course.name}</h1>
        <div className={styles.headerRow}>
          <div className={styles.courseMetadata}>
            <span className={styles.metadataChip}>Course ID: {course.code}</span>
            {course.instructor_first_name && course.instructor_last_name && (
              <span className={styles.metadataChip}>
                Instructor: {course.instructor_first_name} {course.instructor_last_name}
              </span>
            )}
          </div>
          {canAddContent() && (
            <div className={styles.headerActions}>
              <ActionButton className="primary" onClick={handleEditCourse}>
                <FiEdit size={16} />
                Edit Course
              </ActionButton>
              <ActionButton className="primary" onClick={handleManageContent}>
                <FiPlus size={16} />
                Manage Content
              </ActionButton>
            </div>
          )}
        </div>
      </header>

      {/* Mobile ModernBottomNav - Only visible on mobile */}
      <div className={styles.mobileOnly}>
        <ModernBottomNav
          sections={sections}
          offset={100}
          activeTab={null}
          onTabChange={() => {}}
          onOpenJump={handleOpenJumpSheet}
          onSectionSelect={handleSectionSelect}
        />
      </div>

      {/* Shared Jump Sheet (used by both mobile and desktop) */}
      <JumpToSheet
        isOpen={isJumpSheetOpen}
        title="Jump to"
        sections={sections}
        activeId={displayActiveSectionId}
        onClose={handleCloseJumpSheet}
        onSelect={handleSectionSelect}
      />

      {/* Main Content Area - Two column layout on desktop */}
      <div className={styles.contentWrapper}>
        <div className={styles.contentMain}>
          {/* Announcements Section moved to top */}
          <div id="section-announcements" className={styles.sectionAnchor} />
          <section
            id="announcements"
            ref={(el) => (sectionRefs.current['announcements'] = el)}
            className={`${styles.moduleCard} ${highlightedSection === 'announcements' ? styles.highlighted : ''}`}
            aria-labelledby="announcements-heading"
          >
        <div className={styles.moduleHeader}>
          <div className={styles.moduleHeaderLeft}>
            <div className={styles.iconBadge}>
              <FiAlertCircle size={20} />
            </div>
            <h2 id="announcements-heading" className={styles.moduleTitle}>
              Announcements
            </h2>
          </div>
          {canAddContent() && (
            <div className={styles.moduleHeaderRight}>
              <button
                className={styles.outlineButton}
                title="Add Announcement"
                onClick={() => handleManageContent('announcements')}
              >
                <FiPlus size={16} />
              </button>
            </div>
          )}
        </div>
        <hr className={styles.moduleDivider} />
        <div className={styles.moduleBody}>
          {announcements.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No announcements</p>
            </div>
          ) : (
            <ul className={styles.contentList}>
              {announcements.map((announcement) => (
                <li key={announcement.id} className={styles.contentItem}>
                  <div className={styles.contentItemHeader}>
                    <h3 className={styles.contentItemTitle}>{announcement.title}</h3>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                      {announcement.is_pinned && (
                        <span className={`${styles.badge} ${styles.pinnedBadge}`}>
                          <FiStar size={12} />
                          Pinned
                        </span>
                      )}
                      <span className={`${styles.badge} ${styles.priorityBadge}`}>
                        {announcement.priority}
                      </span>
                    </div>
                  </div>
                  <div className={styles.contentItemMeta}>
                    <span>
                      <FiCalendar size={14} />
                      {formatDateForDisplay(announcement.created_at, 'M/d/yyyy')}
                    </span>
                    <span>
                      <FiUser size={14} />
                      {announcement.creator_first_name} {announcement.creator_last_name}
                    </span>
                  </div>
                  {(announcement.content && announcement.content.trim()) || (announcement.description && announcement.description.trim()) ? (
                    <div 
                      className={`${styles.contentItemDescription} ${richTextStyles.richText}`}
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtmlForViewer(announcement.content || announcement.description) 
                      }}
                    />
                  ) : (
                    <div className={styles.contentItemDescription} style={{ fontStyle: 'italic', color: '#5B6470' }}>
                      No content available
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>


      {/* Virtual Link Section */}
      {course.virtual_link && (
        <>
          <div id="section-virtual" className={styles.sectionAnchor} />
          <section
            id="virtual"
            ref={(el) => (sectionRefs.current['virtual'] = el)}
            className={`${styles.moduleCard} ${highlightedSection === 'virtual' ? styles.highlighted : ''}`}
          >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiVideo size={20} />
              </div>
              <h2 className={styles.moduleTitle}>Virtual Classroom</h2>
            </div>
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            <p>Join the live class sessions via Microsoft Teams</p>
          </div>
          <div className={styles.moduleFooter}>
            <a
              href={course.virtual_link}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryButton}
            >
              <FiVideo size={16} />
              Join Teams Meeting
            </a>
          </div>
        </section>
        </>
      )}

      {/* WhatsApp Group Section */}
      {course.whatsapp_group_link && (
        <>
          <div id="section-whatsapp" className={styles.sectionAnchor} />
          <section
            id="whatsapp"
            ref={(el) => (sectionRefs.current['whatsapp'] = el)}
            className={`${styles.moduleCard} ${highlightedSection === 'whatsapp' ? styles.highlighted : ''}`}
          >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiMessageCircle size={20} />
              </div>
              <h2 className={styles.moduleTitle}>WhatsApp Group</h2>
            </div>
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            <p>Join the course WhatsApp group for discussions and updates</p>
          </div>
          <div className={styles.moduleFooter}>
            <a
              href={course.whatsapp_group_link}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryButton}
            >
              <FiMessageCircle size={16} />
              Join WhatsApp Group
            </a>
          </div>
        </section>
        </>
      )}

      {/* Video Playlist Section */}
        <div id="section-recordings" className={styles.sectionAnchor} />
        <section
          id="recordings"
          ref={(el) => (sectionRefs.current['recordings'] = el)}
          className={`${styles.moduleCard} ${highlightedSection === 'recordings' ? styles.highlighted : ''}`}
          aria-labelledby="recordings-heading"
        >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiVolume2 size={20} />
              </div>
              <h2 id="recordings-heading" className={styles.moduleTitle}>
                Course Recordings
                {recordings.length > 0 && (
                  <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#5B6470', marginLeft: '0.5rem' }}>
                    ({recordings.length} recording{recordings.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h2>
            </div>
            {canAddContent() && (
              <div className={styles.moduleHeaderRight}>
                <button
                  className={styles.outlineButton}
                  title="Add Recording"
                  onClick={() => handleManageContent('recordings')}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            )}
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            <VideoPlaylist 
              recordings={recordings}
              onVideoSelect={handleVideoSelect}
              selectedVideo={selectedVideo}
            />
          </div>
        </section>

        {/* Video Player Section */}
        {selectedVideo && (
          <section
            data-section="now-playing"
            className={styles.moduleCard}
          >
            <div className={styles.moduleHeader}>
              <div className={styles.moduleHeaderLeft}>
                <div className={styles.iconBadge}>
                  <FiPlay size={20} />
                </div>
                <h2 className={styles.moduleTitle}>Now Playing: {selectedVideo.title}</h2>
              </div>
              <div className={styles.moduleHeaderRight}>
                <button
                  className={styles.outlineButton}
                  onClick={() => setSelectedVideo(null)}
                >
                  Close Player
                </button>
              </div>
            </div>
            <hr className={styles.moduleDivider} />
            <div className={styles.moduleBody}>
              <VideoPlayer 
                recording={selectedVideo} 
                autoPlay={true}
                onError={(error) => {
                  setNotification({
                    isOpen: true,
                    type: 'error',
                    title: 'Video Error',
                    message: error
                  });
                }}
              />
            </div>
          </section>
        )}


        {/* Syllabus Section */}
        <div id="section-syllabus" className={styles.sectionAnchor} />
        <section
          id="syllabus"
          ref={(el) => (sectionRefs.current['syllabus'] = el)}
          className={`${styles.moduleCard} ${highlightedSection === 'syllabus' ? styles.highlighted : ''}`}
        >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiBookOpen size={20} />
              </div>
              <h2 className={styles.moduleTitle}>Syllabus</h2>
            </div>
            {canAddContent() && (
              <div className={styles.moduleHeaderRight}>
                <button
                  className={styles.outlineButton}
                  title="Add Syllabus Entry"
                  onClick={() => handleManageContent('syllabus')}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            )}
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            {syllabus.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No syllabus available</p>
              </div>
            ) : (
              <ul className={styles.contentList}>
                {syllabus.map((entry) => {
                  const instructorName = [course?.instructor_first_name, course?.instructor_last_name]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li key={entry.id} className={styles.contentItem}>
                    <div className={styles.contentItemHeader}>
                      <h3 className={styles.contentItemTitle}>{entry.title}</h3>
                    </div>
                    {entry.description && (
                      <div className={styles.contentItemDescription}>{entry.description}</div>
                    )}
                    {entry.file_path && (
                      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <a
                          href={`/portal/api/course-files/${entry.file_path.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.primaryButton}
                          style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                        >
                          <FiFileText size={14} />
                          View PDF
                        </a>
                        <a 
                          href={`/portal/api/course-files/${entry.file_path.split('/').pop()}`}
                          download
                          className={styles.outlineButton}
                          style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                        >
                          <FiDownload size={14} />
                          Download
                        </a>
                      </div>
                    )}
                    <div className={styles.contentItemMeta}>
                      <span>
                        <FiClock size={14} />
                        Posted: {formatDateForDisplay(entry.created_at, 'M/d/yyyy h:mm a')}
                      </span>
                      {instructorName && (
                        <span>
                          <FiUser size={14} />
                          {instructorName}
                        </span>
                      )}
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Slides Section */}
        <div id="section-slides" className={styles.sectionAnchor} />
        <section
          id="slides"
          ref={(el) => (sectionRefs.current['slides'] = el)}
          className={`${styles.moduleCard} ${highlightedSection === 'slides' ? styles.highlighted : ''}`}
        >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiFileText size={20} />
              </div>
              <h2 className={styles.moduleTitle}>Slides</h2>
            </div>
            {canAddContent() && (
              <div className={styles.moduleHeaderRight}>
                <button
                  className={styles.outlineButton}
                  title="Add Slide"
                  onClick={() => handleManageContent('slides')}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            )}
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            {slides.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No slides available</p>
              </div>
            ) : (
              <ul className={styles.contentList}>
                {slides.map((slide) => {
                  // Helper function to check if file is viewable (PDF or HTML only)
                  const isViewableFile = (fileNameOrUrl) => {
                    if (!fileNameOrUrl || typeof fileNameOrUrl !== 'string') {
                      return false;
                    }
                    // Extract filename from URL if needed, strip query params and hash
                    const fileName = fileNameOrUrl.split('/').pop().split('?')[0].split('#')[0];
                    // Check extension (case-insensitive)
                    const extension = fileName.toLowerCase().split('.').pop();
                    return extension === 'pdf' || extension === 'html' || extension === 'htm';
                  };

                  const fileName = slide.file_name || slide.file_path?.split('/').pop() || '';
                  const fileUrl = slide.file_path 
                    ? `/portal/api/course-files/${slide.file_path.split('/').pop()}`
                    : slide.url;
                  
                  // Determine button visibility based on requirements:
                  // 1. View + Download for .html, .htm, .pdf that are uploaded (have file_path)
                  // 2. View if only link URL is specified (slide.url exists but no slide.file_path)
                  // 3. Download only for files that are NOT .html, .htm, .pdf and no link URL is specified
                  
                  const hasFileUpload = !!slide.file_path;
                  const hasLinkUrlOnly = !!slide.url && !slide.file_path;
                  const isViewable = isViewableFile(fileName || slide.url || '');
                  
                  // Show View + Download for uploaded PDF/HTML files
                  const showViewAndDownload = hasFileUpload && isViewable;
                  // Show View only if only link URL is specified (no file upload)
                  const showViewOnly = hasLinkUrlOnly;
                  // Show Download only for non-viewable uploaded files with no link URL
                  const showDownloadOnly = hasFileUpload && !isViewable && !slide.url;
                  // Edge case: non-viewable file upload + link URL → show View (for link) + Download (for file)
                  const showViewForLinkAndDownload = hasFileUpload && !isViewable && slide.url;

                  return (
                    <li key={slide.id} className={styles.contentItem}>
                      <div className={styles.contentItemHeader}>
                        <h3 className={styles.contentItemTitle}>{slide.title}</h3>
                      </div>
                      {(slide.file_path || slide.url) && (
                        <>
                          <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {/* View button: Show for uploaded PDF/HTML files, link URL only, or link URL with non-viewable file */}
                            {(showViewAndDownload || showViewOnly || showViewForLinkAndDownload) && (
                              <button
                                className={styles.outlineButton}
                                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                                onClick={() => {
                                  // If there's a link URL, use it; otherwise use file URL
                                  const urlToOpen = slide.url || fileUrl;
                                  window.open(urlToOpen, '_blank');
                                }}
                              >
                                <FiFileText size={14} />
                                View
                              </button>
                            )}
                            {/* Download button: Show for uploaded PDF/HTML files, non-viewable files with no link, or non-viewable files with link */}
                            {(showViewAndDownload || showDownloadOnly || showViewForLinkAndDownload) && (
                              <button
                                className={styles.outlineButton}
                                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = fileUrl;
                                  link.download = slide.file_name || 'slide';
                                  link.click();
                                }}
                              >
                                <FiDownload size={14} />
                                Download
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    <div className={styles.contentItemMeta}>
                      {slide.week && (
                        <span>
                          <FiCalendar size={14} />
                          Week {slide.week}
                        </span>
                      )}
                      <span>
                        <FiClock size={14} />
                        Posted: {formatDateForDisplay(slide.created_at, 'M/d/yyyy h:mm a')} • <FiUser size={14} />
                        {slide.creator_first_name} {slide.creator_last_name}
                      </span>
                    </div>
                    {slide.description && slide.description.trim() && slide.description.trim() !== '<p></p>' && (() => {
                      let cleanDescription = slide.description;
                      cleanDescription = cleanDescription.replace(/<p>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>&nbsp;<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<br\s*\/?>\s*$/gi, '');
                      cleanDescription = cleanDescription.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
                      cleanDescription = cleanDescription.replace(/^\s*<br\s*\/?>\s*/gi, '').replace(/\s*<br\s*\/?>\s*$/gi, '');
                      cleanDescription = cleanDescription.trim();
                      const textContent = cleanDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                      return textContent ? (
                        <div
                          className={`${styles.contentItemDescription} ${richTextStyles.richText}`}
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtmlForViewer(cleanDescription) 
                          }}
                        />
                      ) : null;
                    })()}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Readings Section */}
        <div id="section-readings" className={styles.sectionAnchor} />
        <section
          id="readings"
          ref={(el) => (sectionRefs.current['readings'] = el)}
          className={`${styles.moduleCard} ${highlightedSection === 'readings' ? styles.highlighted : ''}`}
        >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiBookOpen size={20} />
              </div>
              <h2 className={styles.moduleTitle}>Readings</h2>
            </div>
            {canAddContent() && (
              <div className={styles.moduleHeaderRight}>
                <button
                  className={styles.outlineButton}
                  title="Add Reading"
                  onClick={() => handleManageContent('readings')}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            )}
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            {readings.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No readings available</p>
              </div>
            ) : (
              <ul className={styles.contentList}>
                {readings.map((reading) => (
                  <li key={reading.id} className={styles.contentItem}>
                    <div className={styles.contentItemHeader}>
                      <h3 className={styles.contentItemTitle}>{reading.title}</h3>
                    </div>
                    {(() => {
                      const allUrls = [];
                      const seenUrls = new Set();
                      const hasFile = reading.file_path && reading.file_path.trim();
                      
                      if (reading.urls && reading.urls.length > 0) {
                        reading.urls.forEach(urlItem => {
                          if (!seenUrls.has(urlItem.url)) {
                            allUrls.push({ url: urlItem.url, label: 'Open Link' });
                            seenUrls.add(urlItem.url);
                          }
                        });
                      } else if (reading.url) {
                        if (!seenUrls.has(reading.url)) {
                          allUrls.push({ url: reading.url, label: 'Open Link' });
                          seenUrls.add(reading.url);
                        }
                      } else if (reading.description) {
                        const extractedUrls = extractUrlsFromText(reading.description);
                        extractedUrls.forEach(urlItem => {
                          if (!seenUrls.has(urlItem.url)) {
                            allUrls.push({ url: urlItem.url, label: 'Open Link' });
                            seenUrls.add(urlItem.url);
                          }
                        });
                      }
                      
                      return (allUrls.length > 0 || hasFile) ? (
                        <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {allUrls.map((urlItem, index) => (
                            <button
                              key={`url-${index}`}
                              className={styles.outlineButton}
                              style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                              onClick={() => window.open(urlItem.url, '_blank')}
                            >
                              <FiExternalLink size={14} />
                              {urlItem.label}
                            </button>
                          ))}
                          {hasFile && (
                            <button
                              key="file-path"
                              className={styles.outlineButton}
                              style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                              onClick={() => window.open(`/portal/api/course-files/${reading.file_path.split('/').pop()}`, '_blank')}
                            >
                              <FiFileText size={14} />
                              Open Link
                            </button>
                          )}
                        </div>
                      ) : null;
                    })()}
                    <div className={styles.contentItemMeta}>
                      {reading.week ? (
                        <span>
                          <FiCalendar size={14} />
                          Week {reading.week}
                        </span>
                      ) : (
                        <span>
                          <FiCalendar size={14} />
                          Posted: {formatDateForDisplay(reading.created_at, 'M/d/yyyy h:mm a')} • <FiUser size={14} />
                          {reading.creator_first_name} {reading.creator_last_name}
                        </span>
                      )}
                    </div>
                    {reading.description && reading.description.trim() && reading.description.trim() !== '<p></p>' && (() => {
                      let cleanDescription = reading.description;
                      const urlRegex = /(https?:\/\/[^\s]+)/g;
                      cleanDescription = cleanDescription.replace(urlRegex, '').trim();
                      cleanDescription = cleanDescription.replace(/<p>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>&nbsp;<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<\/p>/gi, '');
                      cleanDescription = cleanDescription.replace(/<br\s*\/?>\s*$/gi, '');
                      cleanDescription = cleanDescription.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
                      cleanDescription = cleanDescription.replace(/^\s*<br\s*\/?>\s*/gi, '').replace(/\s*<br\s*\/?>\s*$/gi, '');
                      cleanDescription = cleanDescription.trim();
                      const textContent = cleanDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                      return textContent ? (
                        <div
                          className={`${styles.contentItemDescription} ${richTextStyles.richText}`}
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtmlForViewer(cleanDescription) 
                          }}
                        />
                      ) : null;
                    })()}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />

      {/* Edit Course Modal */}
      {showEditModal && course && (
        <EditCourseModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          course={course}
          onCourseUpdated={handleCourseUpdated}
        />
      )}

      {/* AddContentModal is now rendered conditionally at the top of the component */}

      {/* Attendance Records Section - Only show for students (moved to bottom) */}
      {user?.role === 'student' && (
        <>
          <div id="section-attendance" className={styles.sectionAnchor} />
          <section
            id="attendance"
            ref={(el) => (sectionRefs.current['attendance'] = el)}
            className={`${styles.moduleCard} ${highlightedSection === 'attendance' ? styles.highlighted : ''}`}
          >
          <div className={styles.moduleHeader}>
            <div className={styles.moduleHeaderLeft}>
              <div className={styles.iconBadge}>
                <FiTrendingUp size={20} />
              </div>
              <h2 className={styles.moduleTitle}>Attendance</h2>
            </div>
          </div>
          <hr className={styles.moduleDivider} />
          <div className={styles.moduleBody}>
            <CourseAttendanceRecords 
              courseId={courseId} 
              courseName={course?.name} 
            />
          </div>
        </section>
        </>
      )}

        </div>

        {/* Desktop Section Rail - Only visible on desktop */}
        <SectionRail
          sections={sections}
          activeSectionId={displayActiveSectionId}
          onSectionSelect={handleSectionSelect}
        />
      </div>

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}

    </div>
  );
}

export default CourseDetail;
